// Global roles - matches backend GlobalRole enum
export type GlobalRole = 'admin' | 'supervisor' | 'user' | 'client';

// Role helper constants for permission checks
export const INTERNAL_ROLES: GlobalRole[] = ['admin', 'supervisor', 'user'];
export const MANAGEMENT_ROLES: GlobalRole[] = ['admin', 'supervisor'];
export const SYSTEM_ADMIN_ROLES: GlobalRole[] = ['admin'];

// Helper functions for role checks
export function isInternalRole(role: GlobalRole): boolean {
  return INTERNAL_ROLES.includes(role);
}

export function isManagementRole(role: GlobalRole): boolean {
  return MANAGEMENT_ROLES.includes(role);
}

export function isSystemAdmin(role: GlobalRole): boolean {
  return role === 'admin';
}

// Project member roles - matches backend ProjectMemberRole enum
export type ProjectMemberRole = 'owner' | 'member' | 'client';

// Comment visibility - matches backend CommentVisibility enum
export type CommentVisibility = 'internal' | 'client_visible';

// Task priority levels
export type TaskPriority = 'urgent' | 'high' | 'medium' | 'low';

// Notification types
export type NotificationType =
  | 'task_assigned'
  | 'task_assigned_team'
  | 'task_updated'
  | 'comment_added'
  | 'file_uploaded'
  | 'mention'
  | 'due_soon'
  | 'access_granted'
  | 'access_revoked';

// Entity types for notifications
export type NotificationEntityType = 'task' | 'comment' | 'project' | 'list' | 'team' | 'file';

export interface User {
  id: string;
  email: string;
  displayName: string;
  publicName: string | null;
  avatarUrl: string | null;
  role: GlobalRole;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UserAdminDetails extends User {
  emailNotificationsEnabled: boolean;
  teamIds: string[];
  projectIds: string[];
}

export interface AdminUpdateUserData {
  displayName?: string;
  publicName?: string | null;
  email?: string;
  avatarUrl?: string | null;
  role?: GlobalRole;
  isActive?: boolean;
  emailNotificationsEnabled?: boolean;
}

export interface UserMinimal {
  id: string;
  displayName: string;
  avatarUrl: string | null;
}

export interface TeamMinimal {
  id: string;
  name: string;
  description: string | null;
  memberCount?: number;
}

export interface Project {
  id: string;
  name: string;
  description: string | null;
  color: string;
  icon: string | null;
  isArchived: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  creator?: UserMinimal;
  members?: ProjectMember[];
  statuses?: TaskStatus[];
  _count?: {
    tasks: number;
    members: number;
  };
}

export type ProjectDashboardActivityType =
  | 'task_created'
  | 'comment_created'
  | 'status_changed'
  | 'task_updated';

export interface ProjectDashboardSummary {
  totalTickets: number;
  openTickets: number;
  inProgressTickets: number;
  closedTickets: number;
  overdueTickets: number;
  unassignedTickets: number;
}

export interface ProjectDashboardListRow {
  listId: string;
  listName: string;
  ticketCount: number;
  openCount: number;
  inProgressCount: number;
  closedCount: number;
  lastUpdated: string | null;
  ownerTeamName: string | null;
}

export interface ProjectDashboardActivityItem {
  id: string;
  type: ProjectDashboardActivityType;
  taskId: string;
  taskTitle: string;
  listId: string;
  listName: string;
  actorName: string | null;
  timestamp: string;
  details: string | null;
}

export interface ProjectDashboardStatusChartItem {
  statusId: string;
  statusName: string;
  color: string;
  count: number;
}

export interface ProjectDashboard {
  projectId: string;
  projectName: string;
  projectIdentifier: string;
  summary: ProjectDashboardSummary;
  lists: ProjectDashboardListRow[];
  recentActivity: ProjectDashboardActivityItem[];
  statusChart: ProjectDashboardStatusChartItem[];
}

export interface ProjectMember {
  id: string;
  projectId: string;
  userId: string;
  role: ProjectMemberRole;
  createdAt: string;
  user: UserMinimal & { email?: string };
}

export interface TaskStatus {
  id: string;
  projectId: string;
  listId?: string | null;
  name: string;
  color: string;
  position: number;
  isDoneState: boolean;
  createdAt?: string;
}

export interface List {
  id: string;
  projectId: string;
  name: string;
  position: number;
  usesCustomStatuses: boolean;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
  taskCount?: number;
  /** Count of tasks excluding those in done/closed status (ClickUp-style) */
  activeTaskCount?: number;
  statuses?: TaskStatus[];
}

export interface TaskList {
  id: string;
  name: string;
}

/** Matches backend TaskUserDto (task assignees). Email omitted in some payloads (e.g. search). */
export interface TaskAssigneeUser {
  id: string;
  email?: string;
  displayName: string;
  avatarUrl: string | null;
}

export interface Task {
  id: string;
  projectId: string;
  listId: string;
  statusId: string;
  parentId: string | null;
  title: string;
  description: string | null;
  priority: TaskPriority;
  assignedTeamId: string | null;
  dueDate: string | null;
  position: number;
  isClientVisible: boolean;
  completedAt: string | null;
  mergedIntoId: string | null;
  mergedAt: string | null;
  /** Original ClickUp date_updated for imported tasks; null for native tasks. */
  sourceUpdatedAt: string | null;
  /** Effective last-updated for UI/sorting: max(sourceUpdatedAt, updatedAt). */
  effectiveLastUpdated?: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  /** User assignees (many-to-many). */
  assignees: TaskAssigneeUser[];
  assignedTeam?: TeamMinimal | null;
  creator?: UserMinimal;
  status?: TaskStatus;
  list?: TaskList;
  project?: Pick<Project, 'id' | 'name'>;
  parent?: Pick<Task, 'id' | 'title'> | null;
  subtasks?: Task[];
  /** Number of subtasks the current user cannot access (e.g. in another list). */
  restrictedSubtaskCount?: number;
  comments?: TaskComment[];
  attachments?: Attachment[];
  tags?: TagMinimal[];
  commentCount?: number;
  attachmentCount?: number;
  subtaskCount?: number;
  /** Whether the current user is following this task. */
  isFollowing?: boolean;
  /** Total number of followers. */
  followerCount?: number;
  _count?: {
    subtasks: number;
    comments: number;
    attachments: number;
  };
}

export interface TaskComment {
  id: string;
  taskId: string;
  userId: string;
  content: string;
  visibility: CommentVisibility;
  editedAt: string | null;
  createdAt: string;
  author: UserMinimal;
  attachments?: Attachment[];
}

export interface TaskHistoryEntry {
  id: string;
  action: string;
  message: string;
  createdAt: string;
  actor: UserMinimal | null;
  metadata?: Record<string, unknown>;
}

export interface Attachment {
  id: string;
  taskId: string | null;
  commentId: string | null;
  fileName: string;
  fileSize: number;
  mimeType: string;
  downloadUrl: string;
  createdAt: string;
  uploader?: UserMinimal;
}

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string | null;
  entityType: NotificationEntityType | null;
  entityId: string | null;
  isRead: boolean;
  createdAt: string;
}

export interface AuthTokens {
  accessToken: string;
  expiresIn: number;
}

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page?: number;
    limit: number;
    offset?: number;
    hasMore?: boolean;
    totalPages?: number;
  };
}

// Search types
export interface TaskSearchResult {
  id: string;
  projectId: string;
  listId: string;
  statusId: string;
  title: string;
  description: string | null;
  priority: TaskPriority;
  dueDate: string | null;
  position: number;
  isClientVisible: boolean;
  completedAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  assignees: TaskAssigneeUser[];
  status: Pick<TaskStatus, 'id' | 'name' | 'color'>;
  list: TaskList;
  project: { id: string; name: string; color: string };
  tags?: TagMinimal[];
}

/** GET /search/tasks — matches TaskSearchResponseDto */
export interface TaskSearchResponse {
  query: string | null;
  total: number;
  limit: number;
  offset: number;
  results: TaskSearchResult[];
}

// Invite types
export interface Invite {
  id: string;
  email: string;
  role: GlobalRole;
  inviteLink: string;
  expiresAt: string;
  acceptedAt: string | null;
  createdAt: string;
  invitedBy: {
    id: string;
    displayName: string;
  };
}

export interface InviteListItem {
  id: string;
  email: string;
  role: GlobalRole;
  expiresAt: string;
  acceptedAt: string | null;
  createdAt: string;
  invitedBy: {
    id: string;
    displayName: string;
  };
  isExpired: boolean;
}

export interface ValidateInviteResponse {
  valid: boolean;
  email?: string;
  role?: GlobalRole;
  expiresAt?: string;
  invitedBy?: {
    displayName: string;
  };
  error?: string;
}

// Team types
export interface Team {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  members?: TeamMember[];
  _count?: {
    members: number;
    projects: number;
  };
}

export interface TeamListItem {
  id: string;
  name: string;
  description: string | null;
  memberCount: number;
  projectCount: number;
  createdAt: string;
}

export interface TeamMember {
  id: string;
  userId: string;
  createdAt: string;
  user: {
    id: string;
    email: string;
    displayName: string;
    avatarUrl: string | null;
    role: GlobalRole;
  };
}

export interface ProjectTeam {
  id: string;
  teamId: string;
  createdAt: string;
  team: {
    id: string;
    name: string;
    description: string | null;
    memberCount: number;
  };
}

export type ListMemberAccessSource = 'direct' | 'project' | 'team';

export interface ListMember {
  id: string;
  userId: string;
  createdAt: string;
  user: {
    id: string;
    email: string;
    displayName: string;
    avatarUrl: string | null;
    role: GlobalRole;
  };
  accessSource?: ListMemberAccessSource;
}

// Email Settings types
export interface EmailSettings {
  id: string;
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  hasPassword: boolean;
  smtpSecure: boolean;
  senderName: string;
  senderEmail: string;
  isEnabled: boolean;
  lastTestedAt: string | null;
  lastTestStatus: 'success' | 'failed' | null;
  updatedAt: string;
}

export interface UpdateEmailSettingsData {
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpPassword?: string;
  smtpSecure: boolean;
  senderName: string;
  senderEmail: string;
  isEnabled: boolean;
}

export interface TestEmailResult {
  success: boolean;
  message: string;
}

export type ClickupSyncRunStatus = 'running' | 'success' | 'partial_failure' | 'failed';

export interface ClickupSyncSpace {
  id: string;
  spaceId: string;
  createdAt: string;
}

export interface ClickupSyncRun {
  id: string;
  startedAt: string;
  finishedAt: string | null;
  status: ClickupSyncRunStatus;
  summaryJson: Record<string, unknown> | null;
  spacesJson: string[];
  createdAt: string;
}

export interface ClickupSyncSettings {
  hasToken: boolean;
  tokenLast4: string | null;
  recentOnly: boolean;
  requestsPerSecond: number;
  updatedAt: string | null;
  spaces: ClickupSyncSpace[];
  lastRun: ClickupSyncRun | null;
}

export interface UpdateClickupSyncSettingsData {
  token?: string;
  spaces: string[];
  recentOnly?: boolean;
  requestsPerSecond?: number;
}

export interface ClickupSyncTestResult {
  success: boolean;
  message: string;
  checkedSpaces: Array<{ id: string; name: string }>;
}

export interface ClickupSyncLog {
  id: string;
  runId: string | null;
  timestamp: string;
  level: 'info' | 'warn' | 'error';
  message: string;
}

export interface ClickupSyncLogsResponse {
  logs: ClickupSyncLog[];
}

export interface ClickupAvailableSpace {
  workspaceId: string;
  workspaceName: string;
  spaceId: string;
  spaceName: string;
}

export interface ClickupAvailableSpacesResponse {
  spaces: ClickupAvailableSpace[];
  warnings: string[];
}

// User preferences types
export interface UserPreferences {
  emailNotificationsEnabled: boolean;
}

export interface UpdateUserPreferencesData {
  emailNotificationsEnabled?: boolean;
}

// Capability/Permission types
export interface Capability {
  id: string;
  key: string;
  label: string;
  category: string;
  description: string | null;
  isProtected: boolean;
}

export interface PermissionMatrix {
  capabilities: Capability[];
  matrix: Record<GlobalRole, Record<string, boolean>>;
}

export interface UserCapabilities {
  capabilities: Record<string, boolean>;
}

export interface UpdateRoleCapability {
  role: GlobalRole;
  capabilityKey: string;
  allowed: boolean;
}

// Capability categories for grouping in UI
export const CAPABILITY_CATEGORIES = [
  'Users',
  'Teams',
  'Projects',
  'Lists',
  'Tasks',
  'Statuses',
  'Comments',
  'Attachments',
  'Settings',
  'Permissions',
  'Audit',
  'Chats',
] as const;

export type CapabilityCategory = typeof CAPABILITY_CATEGORIES[number];

// ============================================
// CHAT TYPES
// ============================================

export type ProjectChatType = 'internal' | 'client';

export interface ProjectChat {
  id: string;
  projectId: string;
  type: ProjectChatType;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectChatMessage {
  id: string;
  chatId: string;
  userId: string;
  content: string;
  createdAt: string;
  user: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
  };
}

export interface ProjectChatPermissionOverride {
  id: string;
  chatId: string;
  userId: string;
  canRead: boolean;
  canWrite: boolean;
  user: {
    id: string;
    email: string;
    displayName: string;
    avatarUrl: string | null;
  };
}

export interface ProjectChatAccess {
  canRead: boolean;
  canWrite: boolean;
  canManageSettings: boolean;
  canManageMembers: boolean;
}

export interface ProjectChatSettings {
  chat: ProjectChat;
  defaultAccess: {
    admin: { canRead: boolean; canWrite: boolean };
    supervisor: { canRead: boolean; canWrite: boolean };
    user: { canRead: boolean; canWrite: boolean };
    client: { canRead: boolean; canWrite: boolean };
  };
  overrides: ProjectChatPermissionOverride[];
}

export interface CompanyChat {
  id: string;
  name: string;
  description: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  memberCount?: number;
  unreadCount?: number;
}

export interface CompanyChatMessage {
  id: string;
  chatId: string;
  userId: string;
  content: string;
  createdAt: string;
  user: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
  };
}

export interface CompanyChatMember {
  id: string;
  chatId: string;
  userId: string;
  canWrite: boolean;
  createdAt: string;
  user: {
    id: string;
    email: string;
    displayName: string;
    avatarUrl: string | null;
    role: GlobalRole;
  };
}

export interface CompanyChatAccess {
  canRead: boolean;
  canWrite: boolean;
  canManageSettings: boolean;
  canManageMembers: boolean;
  isMember: boolean;
}

export interface CompanyChatSettings {
  chat: CompanyChat;
  members: CompanyChatMember[];
}

// ============================================
// UNIFIED CHATS
// ============================================

export type ChatCategory = 'company' | 'project';

export interface UnifiedChat {
  id: string;
  name: string;
  category: ChatCategory;
  description?: string | null;
  isPrivate?: boolean;
  projectId?: string;
  projectName?: string;
  chatType?: 'internal' | 'client';
  unreadCount: number;
  createdAt: string;
  updatedAt: string;
}

// ============================================
// TAG SYSTEM
// ============================================

export interface TagSet {
  id: string;
  name: string;
  description: string | null;
  isLegacyImported?: boolean;
  createdAt: string;
  updatedAt: string;
  tags?: Tag[];
  _count?: {
    tags: number;
    listTags: number;
  };
}

export interface Tag {
  id: string;
  tagSetId: string;
  name: string;
  color: string;
  isLegacyImported?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TagMinimal {
  id: string;
  name: string;
  color: string;
  tagSetId: string;
  isLegacyImported?: boolean;
}

export interface ListTagSet {
  id: string;
  listId: string;
  tagSetId: string;
  createdAt: string;
  tagSet: {
    id: string;
    name: string;
    description: string | null;
    isLegacyImported?: boolean;
    _count: {
      tags: number;
    };
  };
}

export interface EffectiveTag {
  id: string;
  name: string;
  color: string;
  tagSetId: string;
  tagSetName: string;
  isLegacyImported?: boolean;
}

export interface TaskTag {
  id: string;
  taskId: string;
  tagId: string;
  createdAt: string;
  tag: {
    id: string;
    name: string;
    color: string;
    tagSetId: string;
    isLegacyImported?: boolean;
  };
}
